import { useJobShiftContext } from '@company/state/jobShiftContext';
import { filterShiftsByDate } from '@services/shift/shiftService';
import { removeDuplicatedShifts } from '@utils/ShiftsUtils';
import { BrowserView, MobileView } from 'react-device-detect';
import { JobAssignment, ShiftPosition } from '@th-types/job.detail.type';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import { ShiftStatus } from '@th-types/jobs.type';
import { Button } from 'react-bootstrap';
import useOnScreen from '@hooks/useIntersectionObserver';
import ShiftTableMobile from './ShiftTableMobile';
import ShiftTable from './ShiftTable';
import './style.scss';

type SortOrder = 'asc' | 'desc';

export interface ShiftSortAttributes {
  key: keyof ShiftPosition;
  order: SortOrder;
}

export const showJobWithNoShiftsMessage = () => {
  return (
    <p>
      You don’t have any shifts on this job. Click the yellow button{' '}
      <b className="text-uppercase">“Create Shift”</b> on this Scheduling tab.
    </p>
  );
};

interface ShiftTablesProps {
  showResumed?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  showOnlyOpenShifts?: boolean;
  openWorkerSideModal?: (jobAssignment: JobAssignment) => void;
  mobileOpenEditShiftModal: (shiftPosition: ShiftPosition) => void;
}

function ShiftTables({
  showResumed = false,
  isEditable = true,
  isSelectable = true,
  showOnlyOpenShifts = false,
  openWorkerSideModal,
  mobileOpenEditShiftModal,
}: ShiftTablesProps) {
  const {
    shifts,
    setSelectedShifts,
    selectedShifts,
    filteredShifts,
    setStatusFilter,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useJobShiftContext();

  const loadMoreRef = useRef(null);
  const isLoadMoreVisible = useOnScreen(loadMoreRef);

  useEffect(() => {
    if (showOnlyOpenShifts) {
      setStatusFilter([ShiftStatus.OPEN]);
    }
  }, [setStatusFilter, showOnlyOpenShifts]);

  useEffect(() => {
    if (
      isLoadMoreVisible &&
      hasNextPage &&
      !isFetchingNextPage &&
      fetchNextPage
    ) {
      fetchNextPage();
    }
  }, [isLoadMoreVisible, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const filterByDate = useMemo(() => {
    return filterShiftsByDate(
      showOnlyOpenShifts
        ? removeDuplicatedShifts(filteredShifts)
        : filteredShifts
    );
  }, [filteredShifts, showOnlyOpenShifts]);

  const handleSelecting = (id: number) => {
    const newCheckedIds = new Set(selectedShifts.map((x) => x.id));
    if (newCheckedIds.has(id)) {
      newCheckedIds.delete(id);
    } else {
      newCheckedIds.add(id);
    }
    const newSelectedShifts = filteredShifts.filter((item) =>
      newCheckedIds.has(item.id)
    );
    setSelectedShifts(newSelectedShifts || []);
  };

  return (
    <>
      {(!shifts || shifts?.length === 0) && (
        <div className="mt-5 d-flex justify-content-center">
          {showJobWithNoShiftsMessage()}
        </div>
      )}
      {filterByDate.map(({ date, data }) => {
        return (
          <Fragment key={date}>
            <MobileView>
              <ShiftTableMobile
                date={date}
                shifts={data}
                showResumed={showResumed}
                handleSelecting={handleSelecting}
                mobileOpenEditShiftModal={mobileOpenEditShiftModal}
              />
            </MobileView>
            <BrowserView>
              <ShiftTable
                date={date}
                shifts={data}
                handleSelecting={handleSelecting}
                openWorkerSideModal={openWorkerSideModal}
                showResumed={showResumed}
                isSelectable={isSelectable}
                isEditable={isEditable}
              />
            </BrowserView>
          </Fragment>
        );
      })}

      {/* Infinite scroll trigger */}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="d-flex justify-content-center mt-4">
          {isFetchingNextPage ? (
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading more shifts...</span>
            </div>
          ) : (
            <Button
              variant="outline-primary"
              onClick={() => fetchNextPage && fetchNextPage()}
              disabled={!hasNextPage}
            >
              Load More Shifts
            </Button>
          )}
        </div>
      )}

      {shifts && shifts.length > 0 && !hasNextPage && !isFetchingNextPage && (
        <div className="d-flex flex-column align-items-center end-of-shifts-message">
          <div className="text-muted">
            <i className="fas fa-check-circle me-2" />
            You&apos;ve reached the end - {shifts.length} shifts for this job.
          </div>
        </div>
      )}
    </>
  );
}

export default ShiftTables;
